import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { NAV_ITEMS } from '../lib/navItems.js';

/**
 * Mobile navigation — a hamburger button plus a slide-in drawer.
 *
 * Below 768px the desktop `.topnav` row and the search trigger are hidden by
 * CSS and live here instead, with their labels visible. The old phone layout
 * kept all four nav links in the bar as bare emoji with `.topnav-label`
 * hidden, which crammed five icons between the wordmark and the project
 * switcher and gave no hint what any glyph did.
 *
 * Button and drawer render at every width — CSS alone decides visibility, so
 * there's no resize listener to keep in sync and no hydration mismatch.
 *
 * The drawer is portalled to <body>. It cannot live inside `.topbar`: that
 * header is `position: sticky` with `z-index: 10`, which makes it a stacking
 * context, so a child's z-index would be capped relative to the header rather
 * than the page — the drawer would slide *under* anything on the page stacked
 * above the topbar (menus at z-index 50, the search modal at 100).
 */
export default function MobileNav({ open, onOpenChange, onOpenSearch }) {
  const location = useLocation();
  const panelRef = useRef(null);
  const toggleRef = useRef(null);
  // Tracks whether the drawer was open on the previous render, so focus is
  // only restored to the toggle on a real open→closed transition. Without
  // this, every unrelated re-render while closed would steal focus.
  const wasOpen = useRef(false);

  // Close on navigation. Clicking a drawer link changes the route; without
  // this the drawer would stay open over the page the user just asked for.
  useEffect(() => {
    onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Esc closes. Bound only while open so we don't hold a global listener for
  // the whole session.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // Lock background scroll while the drawer covers the page. A class rather
  // than an inline style so it can't fight the `overflow-x: clip` that
  // html/body already carry for rogue-element containment.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('mobile-nav-open');
    return () => document.body.classList.remove('mobile-nav-open');
  }, [open]);

  // Move focus into the drawer on open, and back to the hamburger on close.
  useEffect(() => {
    if (open) {
      panelRef.current?.querySelector('a, button')?.focus();
    } else if (wasOpen.current) {
      toggleRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  return (
    <>
      <button
        ref={toggleRef}
        type="button"
        className="nav-toggle"
        onClick={() => onOpenChange(!open)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="mobile-nav-drawer"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          {open ? (
            <>
              <path d="M5 5l14 14" />
              <path d="M19 5L5 19" />
            </>
          ) : (
            <>
              <path d="M3 6h18" />
              <path d="M3 12h18" />
              <path d="M3 18h18" />
            </>
          )}
        </svg>
      </button>

      {open && createPortal(
        <div className="mobile-nav-scrim" onClick={() => onOpenChange(false)}>
          <div
            id="mobile-nav-drawer"
            className="mobile-nav-drawer"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="mobile-nav-list">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`mobile-nav-item${item.isActive(location.pathname) ? ' active' : ''}`}
                >
                  <span className="mobile-nav-item-icon" aria-hidden="true">{item.icon}</span>
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="mobile-nav-divider" />

            <button
              type="button"
              className="mobile-nav-item"
              onClick={() => {
                onOpenChange(false);
                onOpenSearch();
              }}
            >
              <span className="mobile-nav-item-icon" aria-hidden="true">🔍</span>
              Search
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
